// File Location: src/screens/financial/FinancialProfile.jsx

import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Edit,
  Save,
  X,
  Camera,
  Shield,
  Key,
  Bell,
  Globe,
  Clock,
  Activity,
  Target,
  Award,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Settings,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronRight
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { getFinancialPerformanceMetrics } from '../../services/financialservice';

const FinancialProfile = () => {
  const { userData: user, updateUserProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    designation: '',
    department: '',
    employeeId: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    emergencyContact: '',
    dateOfJoining: '',
    specialization: [],
    certifications: []
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [preferences, setPreferences] = useState({
    notifications: {
      email: true,
      sms: false,
      emergencyAlerts: true,
      dailySummary: true,
      weeklyReport: false
    },
    dashboard: {
      theme: 'light',
      language: 'en',
      timezone: 'Asia/Kolkata',
      autoRefresh: true,
      itemsPerPage: 10
    }
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.profile?.name || user.displayName || '',
        email: user.email || '',
        phone: user.profile?.phone || '',
        designation: user.profile?.designation || 'Financial Analyst',
        department: user.profile?.department || 'Financial Services',
        employeeId: user.profile?.employeeId || '',
        address: user.profile?.address || '',
        city: user.profile?.city || '',
        state: user.profile?.state || '',
        pincode: user.profile?.pincode || '',
        emergencyContact: user.profile?.emergencyContact || '',
        dateOfJoining: user.profile?.dateOfJoining || '',
        specialization: user.profile?.specialization || ['KYC Verification', 'Risk Assessment'],
        certifications: user.profile?.certifications || []
      });

      setPreferences(user.preferences || preferences);
      loadPerformanceData();
    }
  }, [user]);

  const loadPerformanceData = async () => {
    if (!user?.uid) return;

    try {
      const result = await getFinancialPerformanceMetrics(user.uid);
      if (result.success) {
        setPerformanceData(result.metrics);
      }
    } catch (error) {
      console.error('Error loading performance data:', error);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await updateUserProfile({
        profile: profileData,
        preferences: preferences
      });
      setEditing(false);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    try {
      // In a real implementation, this would call Firebase Auth password change
      alert('Password change functionality would be implemented here');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordForm(false);
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password');
    }
  };

  const addSpecialization = () => {
    const newSpec = prompt('Enter new specialization:');
    if (newSpec && !profileData.specialization.includes(newSpec)) {
      setProfileData(prev => ({
        ...prev,
        specialization: [...prev.specialization, newSpec]
      }));
    }
  };

  const removeSpecialization = (index) => {
    setProfileData(prev => ({
      ...prev,
      specialization: prev.specialization.filter((_, i) => i !== index)
    }));
  };

  const addCertification = () => {
    const name = prompt('Certification name:');
    const issuer = prompt('Issued by:');
    const date = prompt('Issue date (YYYY-MM-DD):');
    
    if (name && issuer && date) {
      setProfileData(prev => ({
        ...prev,
        certifications: [...prev.certifications, { name, issuer, date }]
      }));
    }
  };

  const removeCertification = (index) => {
    setProfileData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPerformanceLevel = (score) => {
    if (score >= 90) return { level: 'Excellent', color: 'text-green-600 bg-green-100' };
    if (score >= 80) return { level: 'Good', color: 'text-blue-600 bg-blue-100' };
    if (score >= 70) return { level: 'Average', color: 'text-yellow-600 bg-yellow-100' };
    return { level: 'Needs Improvement', color: 'text-red-600 bg-red-100' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
          <p className="text-gray-600">Manage your account settings and preferences</p>
        </div>
        <div className="flex items-center space-x-3">
          {editing ? (
            <>
              <button
                onClick={() => setEditing(false)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Edit className="h-4 w-4" />
              <span>Edit Profile</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.name || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <p className="text-gray-900">{profileData.email}</p>
                <p className="text-xs text-gray-500">Email cannot be changed</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                {editing ? (
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.phone || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.employeeId}
                    onChange={(e) => setProfileData(prev => ({ ...prev, employeeId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.employeeId || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.designation}
                    onChange={(e) => setProfileData(prev => ({ ...prev, designation: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.designation}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.department}
                    onChange={(e) => setProfileData(prev => ({ ...prev, department: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.department}</p>
                )}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Address Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                {editing ? (
                  <textarea
                    value={profileData.address}
                    onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.address || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.city}
                    onChange={(e) => setProfileData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.city || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.state}
                    onChange={(e) => setProfileData(prev => ({ ...prev, state: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.state || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PIN Code</label>
                {editing ? (
                  <input
                    type="text"
                    value={profileData.pincode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, pincode: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.pincode || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact</label>
                {editing ? (
                  <input
                    type="tel"
                    value={profileData.emergencyContact}
                    onChange={(e) => setProfileData(prev => ({ ...prev, emergencyContact: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.emergencyContact || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Professional Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Joining</label>
                {editing ? (
                  <input
                    type="date"
                    value={profileData.dateOfJoining}
                    onChange={(e) => setProfileData(prev => ({ ...prev, dateOfJoining: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <p className="text-gray-900">{formatDate(profileData.dateOfJoining)}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Specializations</label>
                  {editing && (
                    <button
                      onClick={addSpecialization}
                      className="text-sm text-teal-600 hover:text-teal-700"
                    >
                      Add Specialization
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {profileData.specialization.map((spec, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800"
                    >
                      {spec}
                      {editing && (
                        <button
                          onClick={() => removeSpecialization(index)}
                          className="ml-2 text-teal-600 hover:text-teal-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                  {profileData.specialization.length === 0 && (
                    <p className="text-gray-500 text-sm">No specializations added</p>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Certifications</label>
                  {editing && (
                    <button
                      onClick={addCertification}
                      className="text-sm text-teal-600 hover:text-teal-700"
                    >
                      Add Certification
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {profileData.certifications.map((cert, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{cert.name}</p>
                        <p className="text-sm text-gray-600">Issued by {cert.issuer} • {formatDate(cert.date)}</p>
                      </div>
                      {editing && (
                        <button
                          onClick={() => removeCertification(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {profileData.certifications.length === 0 && (
                    <p className="text-gray-500 text-sm">No certifications added</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Security Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Security Settings</h2>
              <button
                onClick={() => setShowPasswordForm(!showPasswordForm)}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Key className="h-4 w-4" />
                <span>Change Password</span>
              </button>
            </div>

            {showPasswordForm && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handlePasswordChange}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    Update Password
                  </button>
                  <button
                    onClick={() => setShowPasswordForm(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Two-Factor Authentication</p>
                    <p className="text-sm text-green-700">Add an extra layer of security</p>
                  </div>
                </div>
                <button className="text-sm text-green-600 hover:text-green-700 font-medium">
                  Enable
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Activity className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Login Activity</p>
                    <p className="text-sm text-gray-600">View recent login activity</p>
                  </div>
                </div>
                <button className="text-sm text-gray-600 hover:text-gray-700 font-medium">
                  View
                </button>
              </div>
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h2>
            
            <div className="space-y-4">
              {Object.entries({
                email: 'Email Notifications',
                sms: 'SMS Notifications',
                emergencyAlerts: 'Emergency Alerts',
                dailySummary: 'Daily Summary',
                weeklyReport: 'Weekly Performance Report'
              }).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{label}</p>
                    <p className="text-sm text-gray-600">
                      {key === 'email' && 'Receive notifications via email'}
                      {key === 'sms' && 'Receive notifications via SMS'}
                      {key === 'emergencyAlerts' && 'Get notified about emergency cases'}
                      {key === 'dailySummary' && 'Daily summary of your activities'}
                      {key === 'weeklyReport' && 'Weekly performance reports'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.notifications[key]}
                      onChange={(e) => setPreferences(prev => ({
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          [key]: e.target.checked
                        }
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar - Performance & Quick Info */}
        <div className="space-y-6">
          {/* Profile Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="h-10 w-10 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{profileData.name || 'Financial Team Member'}</h3>
              <p className="text-gray-600">{profileData.designation}</p>
              <p className="text-sm text-gray-500">{profileData.department}</p>
              
              <div className="mt-4 flex items-center justify-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>Joined {formatDate(profileData.dateOfJoining)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Summary */}
          {performanceData && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Summary</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Processed</span>
                  <span className="font-semibold text-gray-900">{performanceData.totalProcessed}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Approval Rate</span>
                  <span className={`font-semibold px-2 py-1 rounded-full text-xs ${getPerformanceLevel(performanceData.approvalRate).color}`}>
                    {performanceData.approvalRate}%
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Avg Processing Time</span>
                  <span className="font-semibold text-gray-900">{performanceData.averageProcessingTime}min</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Disbursal Efficiency</span>
                  <span className={`font-semibold px-2 py-1 rounded-full text-xs ${getPerformanceLevel(performanceData.disbursalEfficiency).color}`}>
                    {performanceData.disbursalEfficiency}%
                  </span>
                </div>

                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Overall Performance</span>
                    <span className={`font-semibold px-3 py-1 rounded-full text-sm ${getPerformanceLevel((performanceData.approvalRate + performanceData.disbursalEfficiency) / 2).color}`}>
                      {getPerformanceLevel((performanceData.approvalRate + performanceData.disbursalEfficiency) / 2).level}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            
            <div className="space-y-3">
              <button className="w-full flex items-center justify-between p-3 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors">
                <div className="flex items-center space-x-3">
                  <Activity className="h-4 w-4" />
                  <span className="text-sm font-medium">View Performance Report</span>
                </div>
                <ChevronRight className="h-4 w-4" />
              </button>

              <button className="w-full flex items-center justify-between p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                <div className="flex items-center space-x-3">
                  <Bell className="h-4 w-4" />
                  <span className="text-sm font-medium">Notification Settings</span>
                </div>
                <ChevronRight className="h-4 w-4" />
              </button>

              <button className="w-full flex items-center justify-between p-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">
                <div className="flex items-center space-x-3">
                  <Shield className="h-4 w-4" />
                  <span className="text-sm font-medium">Security Settings</span>
                </div>
                <ChevronRight className="h-4 w-4" />
              </button>

              <button className="w-full flex items-center justify-between p-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                <div className="flex items-center space-x-3">
                  <Award className="h-4 w-4" />
                  <span className="text-sm font-medium">Achievements</span>
                </div>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Account Type</span>
                <span className="font-medium text-gray-900">Financial Team</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Member Since</span>
                <span className="font-medium text-gray-900">{formatDate(user?.metadata?.creationTime)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Last Login</span>
                <span className="font-medium text-gray-900">{formatDate(user?.metadata?.lastSignInTime)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Account Status</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Active
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialProfile;